import React from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { CheckCircle2, CloudSun, Compass, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import PremiumYachtHeroVisual from "@/components/public/PremiumYachtHeroVisual";
import PublicHomeSeo from "@/components/public/PublicHomeSeo";
import HomeAdSenseReadinessContent from "@/components/public/HomeAdSenseReadinessContent";
import PublicSeoTopicClusters from "@/components/public/PublicSeoTopicClusters";
import ReviewNavigationShortcuts from "@/components/public/ReviewNavigationShortcuts";
import HomepageClarityPanel from "@/components/public/HomepageClarityPanel";
import ProductUnderstandingSection from "@/components/public/ProductUnderstandingSection";
import { useLanguage } from "@/lib/LanguageContext";
import { homeTranslations } from "@/lib/homeTranslations";

const FreePremiumSplit = React.lazy(() => import("@/components/public/FreePremiumSplit"));
const benefitIcons = [CloudSun, Compass, ShieldCheck];
const dailyBriefingLabels = {
  en: "Daily briefing",
  he: "תדריך יומי",
  es: "Briefing diario",
  it: "Briefing giornaliero",
  fr: "Briefing quotidien",
  de: "Tägliches Briefing",
  pt: "Briefing diário",
  ar: "موجز يومي",
  el: "Ημερήσια ενημέρωση",
  ru: "Ежедневный брифинг",
  tr: "Günlük brifing",
  zh: "每日简报",
};
const disclaimerCopy = {
  en: "Marstick supports skipper judgment and does not replace official forecasts, regulations, or seamanship.",
  he: "Marstick תומכת בשיקול דעת של סקיפר ואינה מחליפה תחזיות רשמיות, תקנות או ימאות מקצועית.",
  es: "Marstick apoya el criterio del patrón y no sustituye previsiones oficiales, normativas ni buena marinería.",
  it: "Marstick supporta il giudizio dello skipper e non sostituisce previsioni ufficiali, normative o buona arte marinaresca.",
  fr: "Marstick soutient le jugement du skipper et ne remplace pas les prévisions officielles, la réglementation ou le sens marin.",
  de: "Marstick unterstützt das Urteilsvermögen des Skippers und ersetzt keine offiziellen Vorhersagen, Vorschriften oder Seemannschaft.",
  pt: "A Marstick apoia o julgamento do skipper e não substitui previsões oficiais, regulamentos ou boa marinharia.",
  ar: "تدعم Marstick حكم الربان ولا تحل محل التوقعات الرسمية أو اللوائح أو مهارات الملاحة البحرية.",
  el: "Το Marstick υποστηρίζει την κρίση του skipper και δεν αντικαθιστά επίσημες προγνώσεις, κανονισμούς ή ναυτοσύνη.",
  ru: "Marstick поддерживает решение шкипера и не заменяет официальные прогнозы, правила или морскую практику.",
  tr: "Marstick kaptan kararını destekler; resmi tahminlerin, kuralların veya denizcilik becerisinin yerini almaz.",
  zh: "Marstick 支持船长判断，但不能替代官方预报、法规或专业航海技能。",
};

export default function PublicHome() {
  const params = useParams();
  const { language } = useLanguage();
  const activeLang = params.lang || language || "en";
  const isRTL = activeLang === "he" || activeLang === "ar";
  const context = useOutletContext() || {};
  const openUpgrade = context.openUpgrade || (() => {});
  const homeCopy = homeTranslations[activeLang] || {};
  const prefix = `/${activeLang}`;
  const heroProof = [homeCopy.builtForSailors, homeCopy.mobileFirstPlanning, homeCopy.freeLearningPremiumTools].filter(Boolean);
  const benefitCards = (homeCopy.editorialCards || []).slice(0, 3).map((card, index) => ({
    icon: benefitIcons[index] || ShieldCheck,
    title: card.title,
    text: card.text,
  }));
  const freePremiumCopy = homeCopy.freePremium || {};
  const primaryHeroCta = homeCopy.startFree || homeCopy.explore || "";
  const secondaryHeroCta = dailyBriefingLabels[activeLang] || "";

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="w-full max-w-full min-w-0 overflow-hidden">
      <PublicHomeSeo />
      <section className="relative border-b border-border bg-[linear-gradient(135deg,hsl(var(--background))_0%,hsl(var(--seafoam)/.58)_46%,hsl(var(--background))_100%)] dark:bg-[radial-gradient(circle_at_25%_12%,rgba(28,167,166,.22),transparent_26rem),linear-gradient(135deg,#06111F_0%,#0B2545_54%,#06111F_100%)]">
        <div className="maritime-container py-6 md:py-10">
          <PremiumYachtHeroVisual isRTL={isRTL} openUpgrade={openUpgrade} unlockLabel={homeCopy.unlockLiveVoyageLayer || homeCopy.explorePremium || ""} imageAlt={homeCopy.heroImageAlt || ""}>
            <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-cyan-100/35 bg-cyan-200/12 px-3 py-1.5 text-xs font-black !text-cyan-50 shadow-sm backdrop-blur-xl">
              <Sparkles className="h-3.5 w-3.5 shrink-0 !text-cyan-100" /> <span className="truncate !text-cyan-50">{homeCopy.homeBadge || ""}</span>
            </div>
            <h1 className="home-compact-hero-title mt-3 max-w-full font-heading font-black tracking-tight text-white drop-shadow-2xl md:mt-4">
              {homeCopy.homeTitle || ""}
            </h1>
            <p className="mt-2 max-w-lg text-sm leading-6 text-cyan-50/88 drop-shadow sm:text-sm md:mt-3 md:text-base md:leading-7 lg:text-lg lg:leading-8">
              {homeCopy.homeIntro || ""}
            </p>
            <div className="mt-4 flex max-w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button asChild className="h-11 min-w-[8rem] rounded-2xl px-6 text-sm font-black text-white"><Link to={`${prefix}/daily-briefing`}>{primaryHeroCta}</Link></Button>
              <Button asChild variant="outline" className="h-11 min-w-[8rem] rounded-2xl border-white/50 bg-white/90 px-6 text-sm font-black text-ocean hover:bg-white hover:text-ocean"><Link to={`${prefix}/daily-briefing`}>{secondaryHeroCta}</Link></Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {heroProof.map((item) => <span key={item} className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-cyan-100/20 bg-cyan-200/12 px-2.5 py-1 text-[11px] font-bold !text-cyan-50 shadow-sm backdrop-blur-xl sm:text-xs"><CheckCircle2 className="h-3.5 w-3.5 shrink-0 !text-cyan-100" /><span className="!text-cyan-50">{item}</span></span>) }
            </div>
            <HomepageClarityPanel prefix={prefix} language={activeLang} />
          </PremiumYachtHeroVisual>
        </div>
      </section>

      <ProductUnderstandingSection lang={activeLang} />

      <ReviewNavigationShortcuts lang={activeLang} />

      <section className="maritime-container maritime-section content-visibility-auto">
        <div className="grid gap-4 md:grid-cols-3">
          {benefitCards.map(({ icon: Icon, title, text }) => (
            <article key={title} className="rounded-[2rem] border border-border bg-card p-6 shadow-xl shadow-ocean/5">
              <Icon className="h-8 w-8 text-primary" />
              <h2 className="mt-4 text-xl font-black">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
            </article>
          ))}
        </div>
        <PublicSeoTopicClusters lang={activeLang} />
      </section>

      <HomeAdSenseReadinessContent lang={activeLang} />

      <div className="content-visibility-auto">
        <React.Suspense fallback={<div className="maritime-container maritime-section min-h-[22rem]" aria-hidden="true" />}>
          <FreePremiumSplit onPremiumClick={openUpgrade} copy={freePremiumCopy} />
        </React.Suspense>
      </div>

      <section className="maritime-container content-visibility-auto pb-10 md:pb-16">
        <div className="rounded-[2rem] border border-primary/20 bg-primary/10 p-5 text-sm font-bold leading-7 text-primary md:p-6">
          {disclaimerCopy[activeLang] || ""}
        </div>
      </section>
    </div>
  );
}